// Live's Consolidate (⌘J): everything a selection covers on a lane becomes
// ONE clip spanning the selection.
//
// As in Live:
// - the selection is a time span (lib/time-selection.ts) or, with clips
//   selected, the span from the earliest of them to the end of the latest,
//   on each lane that holds one;
// - a lane gets one new clip from the selection's start to its end, gaps
//   included — the silence between two clips is part of the new one;
// - a MIDI lane's notes are merged as they PLAY (loops unrolled, markers
//   applied, cut at the span's edges — shared/midi `flattenMidiClip`), so
//   the new clip sounds as the old ones did;
// - an audio lane is rendered: the clips as they sound (warp, pitch, loops),
//   dry — no track devices or mixer, which is also what Live's consolidate
//   leaves out. The render is the caller's (it needs the player); this file
//   says what to render and builds the clip once it has a key.
// - a lane in the selection with nothing on it is left alone.
//
// The clearing and the new clips travel as one gesture: one undo.

import type { Intent } from "@shared/sync/intents.js";
import type { ClipData } from "@shared/sync/protocol.js";
import type { CanonicalClipState, CanonicalRoom } from "@shared/sync/state.js";
import { ASSERT_EPS } from "@shared/sync/canonical.js";
import { canonicalNotes, flattenMidiClip, MAX_NOTES_PER_CLIP, notesDigest, type WireNote } from "@shared/midi/notes.js";
import { mintTempId } from "@web-shared/lib/edits";
import { asGesture, clearSpans } from "@web-shared/lib/live-edits";
import { clipsInSpan, copySpan, isSpan, spanOfClips, type TimeSpan } from "@web-shared/lib/time-selection";

/** What ⌘J acts on: the time selection when there is one, else the selected
 *  timeline clips' extent on their lanes. Null ⇒ nothing to consolidate. */
export function consolidateTarget(
  room: CanonicalRoom,
  timeSelection: TimeSpan | null,
  selectedClips: readonly CanonicalClipState[],
): TimeSpan | null {
  if (isSpan(timeSelection)) return timeSelection;
  return spanOfClips(room, selectedClips);
}

/** One lane's part of a consolidate. */
export interface LaneConsolidate {
  laneId: string;
  kind: "audio" | "midi";
  /** The span's material on this lane, cropped to it. */
  pieces: CanonicalClipState[];
}

export interface ConsolidatePlan {
  span: TimeSpan;
  lanes: LaneConsolidate[];
  /** Lanes whose material can't be carried (an audio clip with no object, a
   *  MIDI clip over the note cap, a cut the wire can't state). */
  refused: string[];
}

/** What a consolidate of `span` would take from each lane. */
export function planConsolidate(room: CanonicalRoom, span: TimeSpan, tempo: number): ConsolidatePlan {
  const lanes: LaneConsolidate[] = [];
  const refused: string[] = [];
  for (const laneId of span.laneIds) {
    const track = room.tracks.find((t) => t.id === laneId);
    if (!track) continue;
    const touching = clipsInSpan(room, { ...span, laneIds: [laneId] });
    if (touching.length === 0) continue;
    const { block, skipped } = copySpan(room, { ...span, laneIds: [laneId] }, tempo);
    if (skipped > 0) {
      refused.push(laneId);
      continue;
    }
    lanes.push({ laneId, kind: (track.trackKind ?? "audio") === "midi" ? "midi" : "audio", pieces: block.clips });
  }
  return { span, lanes, refused };
}

/** The notes a lane's pieces play over the span, relative to its start. */
export function mergedNotes(pieces: readonly CanonicalClipState[], spanStart: number): WireNote[] {
  const merged: WireNote[] = [];
  for (const p of pieces) {
    if (p.muted) continue; // a deactivated clip plays nothing
    const offset = p.startTime - spanStart;
    for (const n of flattenMidiClip(p.notes ?? [], p)) merged.push({ ...n, startTime: n.startTime + offset });
  }
  return canonicalNotes(merged);
}

/** The first piece in time — the new clip takes its name and colour, as Live's does. */
function firstPiece(pieces: readonly CanonicalClipState[]): CanonicalClipState | undefined {
  return [...pieces].sort((a, b) => a.startTime - b.startTime)[0];
}

function createOn(laneId: string, clip: ClipData, audioKey: string | null, prefix: string): Intent {
  return { kind: "upsertClip", id: null, tempId: mintTempId(prefix), trackId: laneId, baseRev: null, clip, audioKey };
}

/** A MIDI lane's consolidated clip. Null when the merge runs over the note cap. */
export function consolidatedMidiClip(lane: LaneConsolidate, span: TimeSpan): Intent | null {
  const first = firstPiece(lane.pieces);
  if (!first) return null;
  const notes = mergedNotes(lane.pieces, span.start);
  if (notes.length > MAX_NOTES_PER_CLIP) return null;
  const length = span.end - span.start;
  return createOn(
    lane.laneId,
    {
      filePath: "",
      startTime: span.start,
      duration: length,
      isWarped: true,
      looping: false,
      startMarker: 0,
      endMarker: length,
      loopStart: 0,
      loopEnd: length,
      name: first.name,
      color: first.color,
      muted: false,
      warpModeValue: 0,
      clipKind: "midi",
      notes,
      notesDigest: notesDigest(notes),
    },
    null,
    "merge",
  );
}

/**
 * An audio lane's consolidated clip, over its render. Warped at the room's
 * tempo (as a bounce in place is) so it follows a tempo change from here on,
 * markers on its own span: the render IS the span, bar for bar.
 */
export function consolidatedAudioClip(lane: LaneConsolidate, span: TimeSpan, audioKey: string, tempo: number): Intent | null {
  const first = firstPiece(lane.pieces);
  if (!first) return null;
  const length = span.end - span.start;
  return createOn(
    lane.laneId,
    {
      filePath: "",
      startTime: span.start,
      duration: length,
      isWarped: true,
      sourceTempo: tempo,
      looping: false,
      startMarker: 0,
      endMarker: length,
      loopStart: 0,
      loopEnd: length,
      name: first.name,
      color: first.color,
      muted: false,
      warpModeValue: 0,
      clipKind: "audio",
    },
    audioKey,
    "consolidate",
  );
}

/**
 * The whole consolidate as one gesture, against the room as it stands now:
 * each consolidated lane's span emptied, then its new clip. `creates` are
 * the new clips (one per lane, built above). Null when something in the way
 * can't be cut where the span's edges fall.
 */
export function consolidateIntents(room: CanonicalRoom, span: TimeSpan, creates: readonly Intent[], tempo: number): Intent[] | null {
  const lanes = creates.flatMap((c) => (c.kind === "upsertClip" ? [c.trackId] : []));
  if (lanes.length === 0) return [];
  const clears = clearSpans(
    room,
    lanes.map((trackId) => ({ trackId, from: span.start, to: span.end })),
    new Set(),
    tempo,
  );
  if (clears === null) return null;
  return asGesture([...clears, ...creates]);
}

/** Whether a span is long enough to make a clip of. */
export function consolidatable(span: TimeSpan | null): span is TimeSpan {
  return span !== null && span.end - span.start > ASSERT_EPS && span.laneIds.length > 0;
}
